import { fontFamily } from 'tailwindcss/defaultTheme';

import {
    createSpacing,
    createWidth,
    extendColours
} from './lib/tailwind/helpers.js';

import {
    expandInteractions,
    typeStyles
} from './lib/tailwind/plugins.js';

/**
 * @type {import('tailwindcss').Config}
 */
export default {
    content: [
        './src/components/**/*.jsx',
        './src/server/templates/pages/main.handlebars'
    ],
    theme: {
        screens: {
            'sm': '768px', // tablet
            'md': '992px', // desktop
            'lg': '1440px' // large desktop
        },
        colors: {},
        fontFamily: {
            'sans': ['"Scto Grotesk A"', ...fontFamily.sans],
            'serif': ['"TWK Ghost"', ...fontFamily.serif]
        },
        spacing: {
            '0': '0',
            ...createSpacing(1),
            ...createSpacing(2),
            ...createSpacing(4),
            ...createSpacing(5),
            ...createSpacing(8),
            ...createSpacing(10),
            ...createSpacing(12),
            ...createSpacing(15),
            ...createSpacing(16),
            ...createSpacing(20),
            ...createSpacing(22),
            ...createSpacing(24),
            ...createSpacing(28),
            ...createSpacing(30),
            ...createSpacing(32),
            ...createSpacing(40),
            ...createSpacing(48),
            ...createSpacing(52),
            ...createSpacing(56),
            ...createSpacing(60),
            ...createSpacing(64),
            ...createSpacing(72),
            ...createSpacing(80),
            ...createWidth(6),
            ...createWidth(8),
            ...createWidth(9)
        },
        typeStyles: ({ theme }) => ({
            'sans-display': {
                displayName: 'sans-display',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 42,
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '110%',
                letterSpacing: '-2.04px',
                md: {
                    fontSize: 62
                }
            },
            'sans-h1': {
                displayName: 'sans-h1',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 28,
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '110%',
                letterSpacing: '-1.44px',
                md: {
                    fontSize: 48
                }
            },
            'sans-h2': {
                displayName: 'sans-h2',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 24,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '125%',
                letterSpacing: '0.12px'
            },
            'sans-h2-med': {
                displayName: 'sans-h2-med',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 24,
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '125%',
                letterSpacing: '0.12px',
                md: {
                    fontSize: 36,
                    letterSpacing: '0.18px'
                }
            },
            'sans-h3': {
                displayName: 'sans-h3',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 20,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '135%',
                letterSpacing: '0.11px',
                md: {
                    fontSize: 22
                }
            },
            'sans-h3-med': {
                displayName: 'sans-h3-med',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 20,
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '135%',
                letterSpacing: '0.11px',
                md: {
                    fontSize: 22
                }
            },
            'sans-body': {
                displayName: 'sans-body',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 16,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '140%',
                letterSpacing: '0.16px'
            },
            'sans-body-med': {
                displayName: 'sans-body-med',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 16,
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '140%',
                letterSpacing: '0.16px'
            },
            'sans-body-s': {
                displayName: 'sans-body-s',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 14,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '140%',
                letterSpacing: '0.14px'
            },

            'serif-h2': {
                displayName: 'serif-h2',
                fontFamily: theme('fontFamily.serif'),
                fontSize: 26,
                fontStyle: 'normal',
                fontWeight: 300,
                lineHeight: '140%',
                letterSpacing: '0.19px',
                md: {
                    fontSize: 38
                }
            },
            'serif-h3': {
                displayName: 'serif-h3',
                fontFamily: theme('fontFamily.serif'),
                fontSize: '24px',
                fontStyle: 'normal',
                fontWeight: 300,
                lineHeight: '140%',
                letterSpacing: '0.12px',
                md: {
                    fontSize: '30px',
                    letterSpacing: '0.15px',
                }
            },
            'serif-h4': {
                displayName: 'serif-h4',
                fontFamily: theme('fontFamily.serif'),
                fontSize: 20,
                fontStyle: 'normal',
                fontWeight: 300,
                lineHeight: '140%',
                letterSpacing: '0.12px',
                md: {
                    fontSize: 24
                }
            },
            'serif-body': {
                displayName: 'serif-body',
                fontFamily: theme('fontFamily.serif'),
                fontSize: 16,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '150%',
                letterSpacing: '0.54px',
                md: {
                    fontSize: 18
                }
            },

            'single-line-body': {
                displayName: 'single-line-body',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 16,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '100%',
                letterSpacing: '0.16px'
            },
            'single-line-eyebrow': {
                displayName: 'single-line-eyebrow',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 12,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '100%',
                letterSpacing: '0.48px',
                textTransform: 'uppercase'
            },

            'pull-quote': {
                displayName: 'pull-quote',
                fontFamily: theme('fontFamily.serif'),
                fontSize: 35,
                fontStyle: 'italic',
                fontWeight: 300,
                lineHeight: '154%',
                letterSpacing: '0.175px',
            },
            'listing-body': {
                displayName: 'listing-body',
                fontFamily: theme('fontFamily.sans'),
                fontSize: 16,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '130%',
                letterSpacing: '-0.16px'
            }
        }),
        extend: {
            borderRadius: {
                DEFAULT: '2px',
                button: '52px',
                'listing-item': '4px'
            },
            gridTemplateColumns: {
                '2-1': '66.67%,33.33%',
                '1-2': '33.33%,66.67%',
            },
            // Build smeantic colour classes from colours file
            colors: { ...extendColours() },
            spacing: {
                'panel-gap-large': 'var(--panel-gap-large)',
                'panel-gap-small': 'var(--panel-gap-small)',
                'panel-pb': 'var(--panel-pb)',
                'panel-pt': 'var(--panel-pt)',
                'panel-px': 'var(--panel-px)'
            }
        }
    },
    plugins: [
        expandInteractions,
        typeStyles
    ]
};
